import {
  AssignmentProposalChangeOp,
  AssignmentProposalStatus,
  type AssignmentProposal,
  type AssignmentProposalChange,
  type AssignmentProposalChangeItem,
  type AssignmentProposalChangeSet,
  type DeleteAssignmentProposalResult,
} from "@models/ISchema";
import type {
  ProposalChangeGroup,
  ProposalChangeLine,
  ProposalDeleteOutcome,
} from "@services/types/assignment-proposal/assignmentProposalListTypes";
import type { ProposalScheduleValue } from "@services/types/assignment-proposal/assignmentProposalTypes";

/** Result of deleting a draft: an errorCode always wins, and only `deleted` is a success. */
export const normalizeDeleteResult = (
  result: DeleteAssignmentProposalResult | null | undefined,
): ProposalDeleteOutcome => {
  if (result?.errorCode) return { kind: "rejected", errorCode: result.errorCode };

  return result?.deleted ? { kind: "deleted" } : { kind: "failed" };
};

type WithStatus = Pick<AssignmentProposal, "status">;

const isDraft = (proposal: WithStatus): boolean =>
  proposal?.status === AssignmentProposalStatus.Draft;

const isRejected = (proposal: WithStatus): boolean =>
  proposal?.status === AssignmentProposalStatus.Rejected;

/**
 * The user's proposals split into drafts and sent ones, the rejected first (they wait for a
 * correction). Otherwise each list keeps the backend order.
 */
export const splitProposalsByTab = <T extends WithStatus>(
  proposals: T[] | null | undefined,
): { sent: T[]; drafts: T[] } => {
  const sent = (proposals ?? []).filter(proposal => !isDraft(proposal));

  return {
    sent: [...sent.filter(isRejected), ...sent.filter(proposal => !isRejected(proposal))],
    drafts: (proposals ?? []).filter(isDraft),
  };
};

/** Code, or name when there is no code (weeks), or id when the record no longer exists. */
const itemLabel = (item: AssignmentProposalChangeItem): string =>
  item?.code || item?.name || String(item?.id);

const resourceLines = (
  set: AssignmentProposalChangeSet | null | undefined,
  addKey: string,
  removeKey: string,
): ProposalChangeLine[] => [
  ...(set?.removed ?? []).map(item => ({ key: removeKey, value: itemLabel(item) })),
  ...(set?.added ?? []).map(item => ({ key: addKey, value: itemLabel(item) })),
];

/** Weeks can be many: one line per operation, names joined by commas. */
const weekLines = (set: AssignmentProposalChangeSet | null | undefined): ProposalChangeLine[] => {
  const joined = (items: AssignmentProposalChangeItem[] | undefined) =>
    (items ?? []).map(itemLabel).join(", ");
  const removed = joined(set?.removed);
  const added = joined(set?.added);

  return [
    ...(removed ? [{ key: "remove-weeks", value: removed }] : []),
    ...(added ? [{ key: "add-weeks", value: added }] : []),
  ];
};

// The JSON scalar reaches the client decoded: anything but an object is no schedule.
const scheduleLine = (key: string, value: unknown): ProposalChangeLine[] =>
  value && typeof value === "object" ? [{ key, schedule: value as ProposalScheduleValue }] : [];

const changeLines = (change: AssignmentProposalChange): ProposalChangeLine[] => {
  if (change.op === AssignmentProposalChangeOp.Removed) {
    const removed = scheduleLine("remove-session", change.schedule?.old);

    return removed.length ? removed : [{ key: "remove-session" }];
  }

  const resources = [
    ...resourceLines(change.classrooms, "add-classroom", "remove-classroom"),
    ...resourceLines(change.instructors, "add-instructor", "remove-instructor"),
    ...weekLines(change.weeks),
  ];

  if (change.op === AssignmentProposalChangeOp.Added) {
    const added = scheduleLine("add-session", change.schedule?.new);

    return [...(added.length ? added : [{ key: "add-session" }]), ...resources];
  }

  return [
    ...scheduleLine("remove-schedule", change.schedule?.old),
    ...scheduleLine("add-schedule", change.schedule?.new),
    ...resources,
  ];
};

/**
 * The changes of a proposal as independent additions and removals, never "A → B": flat when the
 * proposal changes one session, grouped under a session heading when it changes several. Each line
 * names its kind of change (`add-classroom`, `remove-schedule`…) for the view to word it.
 */
export const buildProposalChangeGroups = (
  changes: AssignmentProposalChange[] | null | undefined,
): ProposalChangeGroup[] => {
  const list = changes ?? [];
  const isGrouped = list.length > 1;

  return list
    .map(change => ({
      heading: !isGrouped
        ? null
        : change.op === AssignmentProposalChangeOp.Added
          ? { key: "session-new-heading" }
          : { key: "session-heading", value: String(change.sessionId) },
      lines: changeLines(change),
    }))
    .filter(group => group.lines.length > 0);
};
